import React, {useContext, useEffect} from 'react';
import LazyBody from '@views/knowledge/components/LazyBody';
import DefaultLayout from '@src/components/_layouts/Default/DefaultLayout';
import {LayoutContext} from '@src/contexts/LayoutContext';
import app from '@src/helpers/app';
import {useDispatch} from 'react-redux';
import {articlesFetchArticle, articlesSetActiveId} from '@src/_redux/articles';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import trans from '@src/helpers/trans';
import {LocaleContext} from '@src/contexts/LocaleContext';
import useSelector from '@src/hooks/useSelector';
import usePermissionGranted from '@src/hooks/usePermissionGranted';
import {resolveArticleSlug} from '@views/knowledge/api';

function Body() {
    const dispatch = useDispatch();
    const {setPageBreadcrumbItems, setPageTitle, setPageSubTitle} = useContext(LayoutContext);
    const {locale} = useContext(LocaleContext);
    const params = useParams<{ slug?: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const [articleId, articleUrl] = params?.slug?.split('--', 2) ?? [];

    const article = useSelector(state => {
        return state.articles.articles.find(item => item.id === +articleId);
    });

    // set active article id
    useEffect(() => {
        if (articleId != null) {
            dispatch(articlesSetActiveId(+articleId));
        }
    }, [articleId, dispatch]);

    // fetch model
    useEffect(() => {
        if (article != null && !('content' in article)) {
            dispatch(articlesFetchArticle(articleId));
        }
    }, [article, articleId, dispatch]);

    // correct wrong url
    useEffect(() => {
        if (article != null && article.url !== articleUrl) {
            const correctUrl = app('client-router').getRouteByName('knowledge@show')?.resolveUrl({
                slug: resolveArticleSlug(article),
            });
            if (correctUrl) {
                navigate({
                    ...location,
                    pathname: correctUrl,
                }, {replace: true});
            }
        }
    }, [article, articleUrl, location, navigate]);

    const pageTitle = trans('Articles.update-page-title', article, null, locale);

    const resolver = app('names-resolver').permissions;
    const canEdit = usePermissionGranted(resolver.getUpdateRoutePermissionName('Articles'));

    useEffect(() => {
        setPageTitle(pageTitle);
        setPageSubTitle(canEdit ? 'Articles.update-page-subtitle' : undefined);
        setPageBreadcrumbItems([
            {
                uri: app('client-router').getRouteByName('knowledge@index')?.resolveUrl() ?? '#index',
                label: 'Articles.index-page-title',
            },
            {
                uri: '',
                isInteractive: false,
                label: pageTitle,
            }
        ]);

        return () => {
            setPageTitle(undefined);
            setPageSubTitle(undefined);
            setPageBreadcrumbItems([]);
        };
    }, [canEdit, pageTitle, setPageBreadcrumbItems, setPageSubTitle, setPageTitle]);

    return <LazyBody pageTitle={pageTitle} article={article}/>;
}

export default function UpdatePage() {
    return <DefaultLayout>
        <Body/>
    </DefaultLayout>;
}
